import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Dices, RotateCcw, Loader2, Trophy, Mail, Phone, CheckCircle2, Clock, ExternalLink, Ticket, AlertTriangle, Megaphone, PackageCheck, FileDown, MessageSquareText, Send } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { BasarNav } from "../components/basar/BasarNav";
import { PrizeImage } from "../components/basar/PrizeGallery";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { basarUrl, dashboardBasarCheckUrl, dashboardBasarUrl, nok, useMyBasar } from "../lib/basar";
import { useLang } from "../context/LanguageContext";

// /dashboard/basar/:id/trekning – digital trekning per premie (eitt eksemplar om gongen), trekk om, nullstill, publiser vinnarlista
export default function BasarDrawPage() {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const { basar, emailEnabled, loading, error, reload, setBasar } = useMyBasar(id);
  const [busy, setBusy] = useState(""); // `${prizeId}` | `${prizeId}:${slot}` | "publish" | "reset"
  const [confirm, setConfirm] = useState(null); // "publish" | "reset"
  const [justDrawn, setJustDrawn] = useState(null); // `${prizeId}:${slot}` – for reveal-animasjon
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    if (error === "notfound") navigate("/dashboard/basar");
  }, [error, navigate]);

  const m = basar?.metrics || {};
  const prizes = basar?.prizes || [];
  const draws = Object.fromEntries((basar?.draws || []).map((d) => [d.prize_id, d.winners || []]));
  const published = !!basar?.draw_published_at;
  const sold = m.sold ?? 0;
  const copies = prizes.reduce((s, p) => s + (p.count || 1), 0);
  const drawnCount = Object.values(draws).reduce((s, rows) => s + rows.length, 0);
  const needed = Math.min(copies, sold);
  const allDrawn = prizes.length > 0 && drawnCount >= needed;
  const pending = m.pending ?? 0;
  const canPublish = allDrawn && pending === 0 && !published && basar?.status !== "draft";
  const smsInfo = basar?.sms || { provider: "dummy", simulated: true, enabled: true };
  const allWinners = Object.values(draws).flat();
  const smsMissing = allWinners.filter((w) => !w.sms_sent_at).length;
  const smsFailed = allWinners.filter((w) => w.sms_status === "failed").length;

  const draw = async (prizeId) => {
    setBusy(prizeId);
    try {
      const { data } = await api.post(`/my/basar/${id}/draw/${prizeId}`);
      setJustDrawn(`${prizeId}:${data.slot}`);
      await reload();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const redraw = async (prizeId, slot) => {
    setBusy(`${prizeId}:${slot}`);
    try {
      await api.post(`/my/basar/${id}/draw/${prizeId}/redraw/${slot}`);
      setJustDrawn(`${prizeId}:${slot}`);
      await reload();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const reset = async () => {
    setConfirm(null);
    setBusy("reset");
    try {
      await api.post(`/my/basar/${id}/draw/reset`);
      toast.success(t("Trekningen er nullstilt"));
      await reload();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const publish = async () => {
    setConfirm(null);
    setBusy("publish");
    try {
      const { data } = await api.post(`/my/basar/${id}/draw/publish`);
      setBasar(data.basar);
      toast.success(data.emails > 0 ? t("Vinnerlisten er publisert – {n} vinner(e) får e-post", { n: data.emails }) : t("Vinnerlisten er publisert"));
      if (data.sms_recipients > 0) {
        toast.info(data.sms?.simulated ? t("Vinner-SMS til {n} kjøper(e) er simulert (ingen ekte SMS sendes ennå)", { n: data.sms_recipients }) : t("Vinner-SMS sendes til {n} kjøper(e)", { n: data.sms_recipients }));
      }
      // SMS/e-post går i bakgrunnen – hent status etter eit lite augneblink
      setTimeout(() => reload(), 1500);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  // Vinnar-SMS på nytt (berre til dei som manglar, eller alle)
  const sendSms = async (onlyMissing = true) => {
    setBusy("sms");
    try {
      const { data } = await api.post(`/my/basar/${id}/draw/sms`, { only_missing: onlyMissing });
      if (data.basar) setBasar(data.basar);
      const c = data.counts || {};
      const n = (c.sent || 0) + (c.simulated || 0);
      if (c.recipients === 0) toast.info(t("Alle vinnere har allerede fått SMS"));
      else if (data.sms?.simulated) toast.success(t("SMS til {n} kjøper(e) simulert – ingen ekte SMS sendt", { n }));
      else toast.success(t("SMS sendt til {n} kjøper(e)", { n }));
      if (c.failed) toast.error(t("{n} SMS feilet", { n: c.failed }));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  // Utleveringsliste (PDF) – henta med innlogging og opna i ny fane (fallback: nedlasting)
  const openHandoutList = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/my/basar/${id}/handout-list.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const win = window.open(url, "_blank", "noopener");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `utleveringsliste-${(basar?.name || "basar").toLowerCase().replace(/[^a-z0-9æøå]+/gi, "-").replace(/^-|-$/g, "")}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setPdfBusy(false);
    }
  };

  const reveal = reduced ? {} : { initial: { opacity: 0, y: 6, scale: 0.98 }, animate: { opacity: 1, y: 0, scale: 1 }, transition: { duration: 0.35 } };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="basar-draw-page" data-published={published ? "true" : "false"}>
        <BasarNav basar={basar} />
        {loading || !basar ? (
          <div className="space-y-4" data-testid="basar-loading-state"><Skeleton className="h-24 rounded-xl" />{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-56 rounded-xl" />)}</div>
        ) : (
          <>
            {/* Status for trekninga */}
            <Card className="border-border" data-testid="basar-draw-status">
              <CardContent className="p-5 sm:p-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-1.5 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-heading font-semibold tracking-tight">{t("Trekning")}</h2>
                    {published ? (
                      <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" data-testid="basar-draw-published-badge"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {t("Publisert {date}", { date: formatDateTime(basar.draw_published_at) })}</Badge>
                    ) : (
                      <Badge variant="outline" className="tabular-nums" data-testid="basar-draw-progress-badge">{t("{drawn} av {total} trukket", { drawn: drawnCount, total: needed })}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {published
                      ? t("Vinnerlisten ligger på basarsiden. Salget er stengt og trekningen kan ikke endres.")
                      : t("Hver trekning velger et tilfeldig solgt lodd (kryptografisk tilfeldig) som ikke allerede har vunnet. Ingenting blir offentlig før du publiserer.")}
                  </p>
                  <p className="text-xs text-muted-foreground tabular-nums inline-flex items-center gap-1.5"><Ticket className="w-3.5 h-3.5" /> {t("{sold} solgte lodd · {copies} premier/eksemplarer å trekke", { sold, copies })}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {published ? (
                    <>
                      <Button variant="secondary" onClick={openHandoutList} disabled={pdfBusy} data-testid="basar-draw-handout-list-button">{pdfBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />} {t("Utleveringsliste (PDF)")}</Button>
                      <Button asChild variant="outline" data-testid="basar-draw-public-link"><a href={basarUrl(id)} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1.5" /> {t("Se vinnerlisten")}</a></Button>
                    </>
                  ) : (
                    <>
                      {drawnCount > 0 && <Button variant="ghost" onClick={() => setConfirm("reset")} disabled={busy !== ""} className="text-muted-foreground" data-testid="basar-draw-reset-button"><RotateCcw className="w-4 h-4 mr-1.5" /> {t("Nullstill trekningen")}</Button>}
                      <Button onClick={() => setConfirm("publish")} disabled={!canPublish || busy !== ""} data-testid="basar-draw-publish-button">{busy === "publish" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Megaphone className="w-4 h-4 mr-1.5" />} {t("Publiser vinnerlisten")}</Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {published && allWinners.length > 0 && (
              <Card className={smsInfo.simulated ? "border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/5" : "border-border"} data-testid="basar-draw-sms-panel" data-simulated={smsInfo.simulated ? "true" : "false"}>
                <CardContent className="p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1 min-w-0">
                    <p className="font-heading font-semibold inline-flex items-center gap-2 flex-wrap"><MessageSquareText className="w-4 h-4" /> {t("Vinner-SMS")}
                      {smsInfo.simulated && <Badge variant="outline" className="border-[hsl(var(--info))]/40 text-[hsl(var(--info))]" data-testid="basar-draw-sms-simulated-badge">{t("Simulert")}</Badge>}
                    </p>
                    <p className="text-sm text-muted-foreground" data-testid="basar-draw-sms-summary">
                      {t("{sent} av {total} vinnere har fått SMS med premie, loddnummer og lenke til kvitteringen.", { sent: allWinners.length - smsMissing, total: allWinners.length })}
                      {smsFailed > 0 ? ` ${t("{n} feilet.", { n: smsFailed })}` : ""}
                    </p>
                    {smsInfo.simulated && <p className="text-xs text-muted-foreground" data-testid="basar-draw-sms-simulated-note">{t("SMS-utsending er foreløpig simulert: meldingene logges i Right in, men ingen ekte SMS sendes før en SMS-leverandør er koblet til.")}</p>}
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <Button variant={smsMissing > 0 ? "default" : "outline"} size="sm" onClick={() => sendSms(true)} disabled={busy !== "" || smsMissing === 0} data-testid="basar-draw-sms-missing-button">
                      {busy === "sms" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} {smsMissing > 0 ? t("Send SMS til {n} som mangler", { n: smsMissing }) : t("Alle har fått SMS")}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => sendSms(false)} disabled={busy !== ""} className="text-muted-foreground" data-testid="basar-draw-sms-all-button"><RotateCcw className="w-4 h-4 mr-1.5" /> {t("Send til alle på nytt")}</Button>
                  </div>
                </CardContent>
              </Card>
            )}
            {!published && basar.status === "draft" && <Alert data-testid="basar-draw-draft-note"><AlertDescription>{t("Basaren er en kladd – åpne loddsalget og selg lodd før trekningen.")}</AlertDescription></Alert>}
            {!published && basar.status !== "draft" && sold === 0 && (
              <Alert className="bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30" data-testid="basar-draw-no-sales">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{t("Ingen solgte lodd ennå")}</AlertTitle>
                <AlertDescription>{t("Trekningen kan starte når minst ett lodd er solgt.")}</AlertDescription>
              </Alert>
            )}
            {!published && pending > 0 && (
              <Alert className="bg-[hsl(var(--info))]/10 border-[hsl(var(--info))]/30" data-testid="basar-draw-pending-note">
                <Clock className="h-4 w-4" />
                <AlertTitle>{t("{n} betaling(er) venter på Vipps", { n: pending })}</AlertTitle>
                <AlertDescription>{t("Du kan trekke nå, men vinnerlisten kan først publiseres når alle betalinger er fullført eller kansellert (ubetalte frigis automatisk etter 15 minutter).")}</AlertDescription>
              </Alert>
            )}
            {!published && basar.status === "open" && sold > 0 && (
              <Alert data-testid="basar-draw-open-note">
                <AlertDescription>{t("Loddsalget er fortsatt åpent. Når du publiserer vinnerlisten, stenges salget automatisk.")}{" "}<Link to={dashboardBasarUrl(id)} className="underline underline-offset-4">{t("Steng salget først")}</Link></AlertDescription>
              </Alert>
            )}
            {prizes.length === 0 && (
              <Alert className="bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30" data-testid="basar-empty-state">
                <AlertTitle>{t("Ingen premier ennå")}</AlertTitle>
                <AlertDescription><Link to={dashboardBasarUrl(id, "premiar")} className="underline underline-offset-4">{t("Legg til premier")}</Link></AlertDescription>
              </Alert>
            )}

            {/* Ein panel per premie */}
            <div className="space-y-4" data-testid="basar-draw-prizes">
              {prizes.map((p, idx) => {
                const rows = draws[p.id] || [];
                const count = p.count || 1;
                const slots = Array.from({ length: count }, (_, i) => rows[i] || null);
                const complete = rows.length >= count;
                return (
                  <Card key={p.id} className="border-border overflow-hidden" data-testid="basar-draw-prize-panel" data-prize-id={p.id}>
                    <div className="grid lg:grid-cols-12">
                      <div className="lg:col-span-4 flex gap-4 p-5 bg-muted/30 border-b lg:border-b-0 lg:border-r border-border">
                        <div className="w-24 h-20 rounded-lg overflow-hidden bg-muted shrink-0"><PrizeImage prize={p} /></div>
                        <div className="min-w-0 space-y-1">
                          <p className="text-xs text-muted-foreground">{t("Premie {n}", { n: idx + 1 })}</p>
                          <p className="font-heading font-semibold leading-snug" data-testid="basar-draw-prize-name">{p.name}</p>
                          <div className="flex flex-wrap gap-1.5 text-xs">
                            {p.value > 0 && <Badge variant="outline" className="tabular-nums">{t("Verdi {amount}", { amount: nok(p.value) })}</Badge>}
                            {count > 1 && <Badge variant="outline" className="tabular-nums">× {count}</Badge>}
                            {complete && <Badge className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/15 border-0">{t("Ferdig trukket")}</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="lg:col-span-8 p-5 space-y-3">
                        {slots.map((w, i) => {
                          const slot = i + 1;
                          const key = `${p.id}:${slot}`;
                          if (!w) {
                            const isNext = i === rows.length;
                            return (
                              <div key={key} className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-border p-4" data-testid="basar-draw-slot-empty">
                                <p className="text-sm text-muted-foreground">{count > 1 ? t("Eksemplar {slot} av {count}", { slot, count }) : t("Vinner")}</p>
                                {isNext && !published && (
                                  <Button onClick={() => draw(p.id)} disabled={busy !== "" || sold === 0} data-testid="basar-draw-reveal-button">
                                    {busy === p.id ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Dices className="w-4 h-4 mr-1.5" />} {t("Trekk vinner")}
                                  </Button>
                                )}
                              </div>
                            );
                          }
                          return (
                            <motion.div key={`${key}-${w.number}`} {...(justDrawn === key ? reveal : {})} className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="basar-draw-winner-card" data-number={w.number}>
                              <div className="flex items-center gap-4 min-w-0 flex-1">
                                <div className="text-center shrink-0">
                                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("Vinnerlodd")}</p>
                                  <p className="text-4xl font-heading font-semibold tabular-nums leading-none" data-testid="basar-draw-winner-number">{w.number}</p>
                                </div>
                                <div className="min-w-0 text-sm space-y-0.5">
                                  <p className="font-medium truncate" data-testid="basar-draw-winner-name">{w.name || t("Ukjent kjøper")}</p>
                                  <p className="text-muted-foreground tabular-nums inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {w.phone}</p>
                                  {w.email && <p className="text-muted-foreground inline-flex items-center gap-1.5 break-all"><Mail className="w-3.5 h-3.5 shrink-0" /> {w.email}{published && (w.email_sent_at ? <span className="text-[hsl(var(--success))] text-xs ml-1">{t("e-post sendt")}</span> : emailEnabled ? <span className="text-xs ml-1">{t("sender …")}</span> : null)}</p>}
                                  <p className="text-xs text-muted-foreground">{t("Ordre {number}", { number: w.order_number || "–" })} · {formatDateTime(w.drawn_at)}</p>
                                  {published && (
                                    <p className="text-xs inline-flex items-center gap-1.5" data-testid="basar-draw-winner-sms" data-sms-status={w.sms_status || "missing"}>
                                      <MessageSquareText className="w-3.5 h-3.5 text-muted-foreground" />
                                      {w.sms_status === "failed" ? <span className="text-[hsl(var(--danger))]">{t("SMS feilet")}</span>
                                        : w.sms_sent_at ? <span className="text-[hsl(var(--success))]">{w.sms_status === "simulated" ? t("SMS simulert {date}", { date: formatDateTime(w.sms_sent_at) }) : t("SMS sendt {date}", { date: formatDateTime(w.sms_sent_at) })}</span>
                                        : <span className="text-muted-foreground">{t("SMS ikke sendt")}</span>}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {!published && (
                                <Button variant="secondary" size="sm" onClick={() => redraw(p.id, slot)} disabled={busy !== ""} className="shrink-0 self-start sm:self-center" data-testid="basar-draw-redraw-button">
                                  {busy === key ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />} {t("Trekk på nytt")}
                                </Button>
                              )}
                              {published && (
                                w.handed_out_at ? (
                                  <Badge className="shrink-0 self-start sm:self-center bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 hover:bg-[hsl(var(--success))]/15" data-testid="basar-draw-handed-out-badge"><PackageCheck className="w-3.5 h-3.5 mr-1" /> {t("Utlevert")}</Badge>
                                ) : (
                                  <Button asChild variant="ghost" size="sm" className="shrink-0 self-start sm:self-center text-muted-foreground" data-testid="basar-draw-handout-link">
                                    <Link to={dashboardBasarCheckUrl(id, w.order_id)}><Trophy className="w-4 h-4 mr-1.5 text-[hsl(var(--warning))]" /> {t("Utlever")}</Link>
                                  </Button>
                                )
                              )}
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="basar-draw-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "publish" ? t("Publisere vinnerlisten?") : t("Nullstille trekningen?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "publish"
                ? t("Vinnerlisten blir synlig på basarsiden, loddsalget stenges, og vinnerne får beskjed på SMS (og e-post der den er oppgitt). Dette kan ikke angres.")
                : t("Alle trekninger slettes og du kan trekke på nytt. Ingenting er publisert ennå.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="basar-draw-confirm-cancel">{t("Avbryt")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirm === "publish" ? publish : reset} data-testid="basar-draw-confirm-ok">{confirm === "publish" ? t("Publiser") : t("Nullstill")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
